import { LeadOpsMatch } from '../../../api/leadOps';
import { getInventoryStatus } from '../components/CrmPanel';

/** Canonical Monday Ad Format* values used to detect "never tried" gaps. */
export const AD_FORMAT_REFERENCE = [
  'Banners',
  'Popunders',
  'Tab',
  'Top bar',
  'Native',
  'Pre-Rolls',
  'Video slider',
  'Interstitials',
  'Inpage Push',
  'Widget',
  'Forum Post',
  'Instant Message',
] as const;

export const ARCHIVED_STATUSES = ['finished', 'failed', 'delete'] as const;
export const SELLABLE_STATUSES = ['ready to sell', 'critical to sell'] as const;

export type AdFormatTriedDetail = {
  label: string;
  finishedCount: number;
  failedCount: number;
};

export type AdFormatGapBuckets = {
  active: string[];
  tried: string[];
  triedDetails: AdFormatTriedDetail[];
  never: string[];
};

const UNMAPPED_FORMATS = new Set(['other']);

const normalizeTitle = (title: string) => title.trim().toLowerCase().replace(/\*+$/, '');

const getColumnText = (columns: LeadOpsMatch['columns_preview'] = [], title: string) => {
  const normalizedTitle = normalizeTitle(title);
  const match = columns.find((col) => normalizeTitle(col?.title || '') === normalizedTitle);
  return match?.text || '';
};

export const normalizeAdFormat = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

export const parseAdFormats = (text: string): string[] =>
  text
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const isArchivedStatus = (status: string): boolean =>
  ARCHIVED_STATUSES.includes(status.trim().toLowerCase() as (typeof ARCHIVED_STATUSES)[number]);

const isUnmappedFormat = (normalized: string): boolean => UNMAPPED_FORMATS.has(normalized);

export const isSellableInventory = (match: LeadOpsMatch): boolean => {
  const status = getInventoryStatus(match).trim().toLowerCase();
  return SELLABLE_STATUSES.includes(status as (typeof SELLABLE_STATUSES)[number]);
};

export const isArchivedInventory = (match: LeadOpsMatch): boolean =>
  isArchivedStatus(getInventoryStatus(match));

const displayLabelForFormat = (raw: string, referenceByNormalized: Map<string, string>): string => {
  const normalized = normalizeAdFormat(raw);
  return referenceByNormalized.get(normalized) || raw.trim();
};

export const buildAdFormatGaps = (matches: LeadOpsMatch[]): AdFormatGapBuckets => {
  const referenceByNormalized = new Map(
    AD_FORMAT_REFERENCE.map((label) => [normalizeAdFormat(label), label]),
  );

  const activeNormalized = new Set<string>();
  const triedNormalized = new Set<string>();
  const activeLabels = new Map<string, string>();
  const triedLabels = new Map<string, string>();
  const finishedCounts = new Map<string, number>();
  const failedCounts = new Map<string, number>();

  for (const match of matches) {
    const status = getInventoryStatus(match).trim().toLowerCase();
    const formats = parseAdFormats(getColumnText(match.columns_preview, 'Ad Format'));
    if (!formats.length) continue;

    const archived = isArchivedStatus(status);
    for (const raw of formats) {
      const normalized = normalizeAdFormat(raw);
      if (!normalized || isUnmappedFormat(normalized)) continue;

      const label = displayLabelForFormat(raw, referenceByNormalized);
      if (archived) {
        if (status === 'finished') {
          finishedCounts.set(normalized, (finishedCounts.get(normalized) || 0) + 1);
        } else {
          failedCounts.set(normalized, (failedCounts.get(normalized) || 0) + 1);
        }
        if (!activeNormalized.has(normalized)) {
          triedNormalized.add(normalized);
          if (!triedLabels.has(normalized)) triedLabels.set(normalized, label);
        }
      } else {
        activeNormalized.add(normalized);
        activeLabels.set(normalized, label);
        triedNormalized.delete(normalized);
        triedLabels.delete(normalized);
      }
    }
  }

  const active = Array.from(activeLabels.values()).sort((a, b) => a.localeCompare(b));
  const triedDetails: AdFormatTriedDetail[] = Array.from(triedLabels.entries())
    .map(([normalized, label]) => ({
      label,
      finishedCount: finishedCounts.get(normalized) || 0,
      failedCount: failedCounts.get(normalized) || 0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const tried = triedDetails.map((detail) => detail.label);
  const seen = new Set([...Array.from(activeNormalized), ...Array.from(triedNormalized)]);
  const never = AD_FORMAT_REFERENCE.filter((label) => !seen.has(normalizeAdFormat(label)));

  return { active, tried, triedDetails, never: [...never] };
};

export const sortInventoryForAdvertiser = (matches: LeadOpsMatch[]): LeadOpsMatch[] => {
  const sellableRank = (match: LeadOpsMatch) => {
    const status = getInventoryStatus(match).trim().toLowerCase();
    if (status === 'critical to sell') return 0;
    if (status === 'ready to sell') return 1;
    return 2;
  };
  return [...matches].sort((a, b) => {
    const rankDiff = sellableRank(a) - sellableRank(b);
    if (rankDiff !== 0) return rankDiff;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
};
